﻿import React, { useState, useEffect } from 'react';
import GlobalDialog from '../Modal/GlobalDialog';
import { PackagingMaterial } from '../../models/Production/packaging-material';
import { ModalMetadata } from '../../models/modal-metadata';
import { ModalMessageType } from '../../models/enums';
import { ItemBasicInformation } from '../../models/item-basic-information';
import { ToastProps } from '../../models/toast-props';
import { CrudFunctions } from '../../utilities/crud-functions';
import ItemBasicInformationDialog from '../ItemBasicInformationDialog';
import DmaFooter from '../DmaFooter';

import DataGrid, {
    Column,
    FilterRow,
    Selection,
    Button as ColumnButton,
    HeaderFilter,
    Toolbar,
    Popup,
    Form,
    Item as ToolbarItem,
    Editing,
    Paging,
    Pager,
    SearchPanel,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Item } from 'devextreme-react/form';
import Button from 'devextreme-react/button';
import Toast from 'devextreme-react/toast';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 0;

export default function PackagingMaterialsMaintenance() {
    const [packagingMaterials, setPackagingMaterials] =
        useState<PackagingMaterial[]>();

    const [selectedPackagingMaterial, setSelectedPackagingMaterial] =
        useState<PackagingMaterial>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    useEffect(() => {
        getAllData();
    }, []);

    key++;

    return (
        <div className="pw-content">
            <div className="lq-title-bar pw-form-header">
                <div className="lq-main-title">Packaging Materials</div>

                <div className="title-bar-spacer" />
                <div className="save-toolbar">
                    <Button id="goBackButton" text="" icon="arrowleft" type="normal"
                        hint="go back"
                        height={'2.6em'}
                        onClick={goBack} />
                </div>
            </div>

            <div className="pw-margin-top-05">
                <DataGrid
                    height="20rem" width="100%"
                    dataSource={packagingMaterials ? packagingMaterials : []}
                    wordWrapEnabled={true}
                    allowColumnResizing={true}
                    columnResizingMode='widget'
                    keyExpr="packagingMaterialId"
                    rowAlternationEnabled={true}
                    showRowLines={true}
                    onSelectionChanged={packagingMaterialsSelectionChanged}
                    onRowValidating={onPackagingMaterialsValidating}
                    onRowRemoving={onPackagingMaterialsDelete}
                    showBorders={true}>

                    <LoadPanel enabled={true} />
                    <Paging defaultPageSize={30} />
                    <Pager
                        showPageSizeSelector={true}
                        allowedPageSizes={[30, 60, 100]}
                        showInfo={true} />
                    <Selection mode='single' />
                    <HeaderFilter visible={true} />
                    <FilterRow visible={true} />

                    <SearchPanel visible={true} />

                    <Column type="buttons" width="7rem">
                        <ColumnButton hint="audit information" icon="clock"
                            onClick={onMethodAuditButtonClicked} />
                        <ColumnButton hint="information" icon="info"
                            onClick={onMethodInformationButtonClicked} />
                        <ColumnButton name="delete" />
                    </Column>

                    <Column dataField='name' dataType='string' width='10rem' />
                    <Column dataField='description' dataType='string' />

                    <Toolbar>
                        <ToolbarItem location='after' name="searchPanel" />
                        <ToolbarItem location='after' name="addRowButton" />
                    </Toolbar>

                    <Editing mode="popup"
                        useIcons={true}
                        allowAdding={true}
                        allowUpdating={false}
                        allowDeleting={true}>

                        <Popup title="New Packaging Material" showTitle={true} width="80vw" height="80vh" />
                        <Form>
                            <Item dataField="name" />
                            <Item dataField="description" colSpan={2} />
                        </Form>
                    </Editing>

                </DataGrid>
            </div>

            <DmaFooter />

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width='25rem'
                    height='21rem'
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div>
    );

    function packagingMaterialsSelectionChanged(e: any) {
        if (e.selectedRowsData && e.selectedRowsData.length > 0)
            setSelectedPackagingMaterial(e.selectedRowsData[0]);
    }

    async function onPackagingMaterialsValidating(e: any) {
        if (e.newData) {
            let packagingMaterial = e.newData as PackagingMaterial;

            await insertPackagingMaterial(packagingMaterial)
                .then(() => {
                    showToast("Packaging material saved", "success");
                    getPackagingMaterials();
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Inserting Packaging Material");
                });
        }
    }

    async function onPackagingMaterialsDelete(e: any) {
        if (e.data && e.data.packagingMaterialId) {
            await deletePackagingMaterial(e.data.packagingMaterialId)
                .then(() => {
                    showToast("Packaging Material Deleted Successfully", "success");
                })
                .catch((res) => {
                    showMessage(res, ModalMessageType.Error, "Error Deleting Packaging Material");
                });
        }
    }

    async function getPackagingMaterials() {
        await CrudFunctions.getData("products/packagingmaterials")
            .then(data => {
                setPackagingMaterials(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Packaging Materials");
            });
    }

    /*
    async function getTechnologies() {
        await CrudFunctions.getData("common/benchsheettechnologytypes")
            .then(data => {
                setTechnologies(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Technology List");
            });
    }
    */


    async function insertPackagingMaterial(packagingMaterial: PackagingMaterial) {
        return await CrudFunctions.postData("products/packagingmaterial", packagingMaterial);
    }

    async function updatePackagingMaterial(packagingMaterial: PackagingMaterial) {
        return await CrudFunctions.putData("products/packagingmaterial", packagingMaterial);
    }

    async function deletePackagingMaterial(packagingMaterialId: string) {
        return await CrudFunctions.deleteData(`products/packagingMaterial/${packagingMaterialId}`);
    }

    async function getAllData() {
        await Promise.all([
            //getTechnologies(),
            getPackagingMaterials()
        ])
    }

    //navigation

    function goBack() {
        history.back();
    }

    function onMethodAuditButtonClicked(e: any) {

    }

    function onMethodInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            const packagingMaterial = e.row.data as PackagingMaterial;

            if (packagingMaterial) {
                setSelectedPackagingMaterial(packagingMaterial);

                let itemInformation: ItemBasicInformation = {
                    rowPointer: packagingMaterial.packagingMaterialId,
                    createTimestamp: packagingMaterial.createTimestamp,
                    lastModifiedTimestamp: packagingMaterial.modifyTimestamp,
                    createUser: packagingMaterial.createUser,
                    modifyUser: packagingMaterial.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}